import React, { useCallback, useEffect, useState, useRef } from 'react';
import { PriceGroupsSettings as PriceGroupsApi } from '../../../api/services/next';
import { onError, onSuccess, onWarning } from '../../../components/shared/messages';
import { useTextResources, useMessageResources } from '../../../language';
import GridSearchTemplate from '../../GridSearchTemplate';
import { onUpdateGridHandler } from '../../../components/GridPanel/utility';
import { MenuActions } from '../../../components/shared/constants';
import Checkbox from '../../../components/shared/controlled/Checkbox';
import CheckboxWrapper from '../../../components/shared/Checkbox';
import TextInput from '../../../components/shared/TextInput';
import { useLoader } from '../../../hooks/useLoader';
import './style.less';

const PriceGroupsSettings = () => {
  const text = useTextResources();
  const message = useMessageResources();
  const loader = useLoader();
  const [gridData, setGridData] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const searchSpecRef = useRef({ InActive: false });

  const handleSearchChange = useCallback((spec) => {
    searchSpecRef.current = spec;
  }, []);

  const handleFieldChange = useCallback((row, fieldName, value) => {
    const id = row.Id || row.TempId;

    setGridData(prev => {
      const updated = [...prev];
      const toUpdate = updated.find(r => r.Id === id || r.TempId === id);

      if (toUpdate) {
        toUpdate[fieldName] = value;
      }

      return updated;
    });
  }, []);

  const onGridUpdate = (added, changed, deleted) => {
    if (deleted?.length) {
      const deletedItems = gridData.filter((x, i) => deleted.includes(i));
      setDeleted(prev => [...prev, ...deletedItems.filter(x => x.Id)]);
    }
    const updated = onUpdateGridHandler(gridData, added, changed, deleted);
    setGridData(updated);
  };

  const save = () => {
    (async () => {
      if (gridData?.length) {
        if (gridData.some(r => !r.Name || r.Name.trim() === '')) {
          onWarning(text('OneOrMoreRowsHaveAMissingName'));
          return;
        }

        // Check for duplicate names (case-insensitive)
        const names = gridData.map(r => r.Name?.toLowerCase().trim());
        const duplicateSet = new Set();
        names.forEach((name, index) => {
          if (name && names.indexOf(name) !== index) {
            const originalName = gridData.find(r => r.Name?.toLowerCase().trim() === name)?.Name;
            if (originalName) duplicateSet.add(originalName);
          }
        });

        if (duplicateSet.size > 0) {
          const duplicateList = [...duplicateSet].join(', ');
          onWarning(message('NameMustBeUniqueWithinChain').replace('{0}', duplicateList));
          return;
        }
      }

      try {
        loader(true);
        // Only send deleted items that have a real numeric Id (were saved to DB)
        // Newly added then deleted rows only have TempId, not Id
        const deletedToSend = deleted
          .filter(x => x.Id && typeof x.Id === 'number')
          .map(x => ({ ...x, Id: -x.Id }));
        await PriceGroupsApi.SavePriceGroups([...gridData, ...deletedToSend]);
        setDeleted([]);
        // Refresh grid data from server to get actual IDs for newly created records
        const refreshedData = await PriceGroupsApi.Search(searchSpecRef.current);
        setGridData(refreshedData || []);
        onSuccess();
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    })();
  };

  useEffect(() => {
    if (window.optima?.commands) {
      window.optima.commands.unsubscribe(MenuActions.save);
      window.optima.commands.subscribe(MenuActions.save, save);
      return () => window.optima.commands.unsubscribe(MenuActions.save);
    }
  }, [gridData, deleted]);

  const isActiveFormatter = ({ row }) => {
    return (
      <Checkbox
        checked={row.IsActive}
        onChange={(val) => {
          handleFieldChange(row, 'IsActive', val);
        }}
      />
    );
  };

  const createTextFormatter = (property, maxLength) => {
    return ({ row }) => {
      return (
        <TextInput
          value={row[property]}
          onBlur={(val) => handleFieldChange(row, property, val)}
          maxLength={maxLength}
          style={{ width: '100%' }}
        />
      );
    };
  };

  const gridOptions = {
    search: {
      api: PriceGroupsApi.Search,
      searchOnLoad: true,
      fields: [
        <CheckboxWrapper
          label={text('IncludeInactive')}
          key='InActive'
        />,
      ]
    },
    grid: {
      columns: [
        {
          name: 'Name',
          title: text('Name'),
          formatter: createTextFormatter('Name', 255),
        },
        {
          name: 'IsActive',
          title: text('Active'),
          formatter: isActiveFormatter,
        }
      ],
      editable: {
        allowAdd: true,
        allowDelete: true,
        extraProps: {
          addedRows: [],
          onAddedRowsChange: addedRows => {
            setGridData(prev => [...addedRows.map(row => ({
              ...row,
              TempId: `newPriceGroupId-${crypto.randomUUID()}`,
              IsActive: true,
            })), ...prev]);
          }
        }
      },
      filtering: {
        extensions: [
          { columnName: 'IsActive', filteringEnabled: false },
        ],
      },
      showFilter: false,
      showDeleteMultiple: true,
      selectable: true,
      columnExtensions: [
        { columnName: 'IsActive', width: 100 },
      ],
    }
  };

  return (
    <GridSearchTemplate
      options={gridOptions}
      onGridUpdate={onGridUpdate}
      onSearchComplete={setGridData}
      onSearchChange={handleSearchChange}
      data={gridData}
    />
  );
};

export default PriceGroupsSettings;
